import { useState, useMemo, useEffect } from 'react';
import { useWorkspaces } from '@/contexts/WorkspaceContext';
import { useListDeletedContacts, useRestoreContacts } from '@/hooks/useDeletedContacts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Loader2, RotateCcw, AlertCircle, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function DeletedContactsSettings() {
  const { currentWorkspace } = useWorkspaces();
  const workspaceId = currentWorkspace?.id;

  const {
    data: deletedContacts,
    isLoading,
    isError,
    error,
  } = useListDeletedContacts(workspaceId);

  const restoreContacts = useRestoreContacts();

  // Track which contact is currently being restored (single)
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // Track selected contacts for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Track bulk restore in progress
  const [isBulkRestoring, setIsBulkRestoring] = useState(false);

  // Track IDs of contacts that have been restored in this session
  const [restoredIds, setRestoredIds] = useState<Set<string>>(new Set());

  // Clear restoredIds when deletedContacts data changes (e.g., on refetch)
  // This ensures that if a contact is truly gone from the list, we don't track it anymore
  useEffect(() => {
    if (deletedContacts) {
      setRestoredIds((prev) => {
        const stillInList = new Set(deletedContacts.map((c) => c.id));
        const filtered = new Set([...prev].filter((id) => stillInList.has(id)));
        // Only update if there's a change
        if (filtered.size !== prev.size) {
          return filtered;
        }
        return prev;
      });
    }
  }, [deletedContacts]);

  // Check if all visible contacts are selected
  const allSelected = useMemo(() => {
    if (!deletedContacts || deletedContacts.length === 0) return false;
    return deletedContacts.every((contact) => selectedIds.has(contact.id));
  }, [deletedContacts, selectedIds]);

  // Check if some (but not all) are selected
  const someSelected = useMemo(() => {
    if (!deletedContacts || deletedContacts.length === 0) return false;
    const selectedCount = deletedContacts.filter((c) => selectedIds.has(c.id)).length;
    return selectedCount > 0 && selectedCount < deletedContacts.length;
  }, [deletedContacts, selectedIds]);

  const handleSelectAll = (checked: boolean) => {
    if (checked && deletedContacts) {
      setSelectedIds(new Set(deletedContacts.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (contactId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(contactId);
      } else {
        next.delete(contactId);
      }
      return next;
    });
  };

  const handleRestore = async (contactId: string) => {
    if (!workspaceId) return;

    setRestoringId(contactId);
    try {
      await restoreContacts.mutateAsync({
        contactIds: [contactId],
        workspaceId,
      });
      // Mark as restored and remove from selection
      setRestoredIds((prev) => new Set([...prev, contactId]));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
    } finally {
      setRestoringId(null);
    }
  };

  const handleBulkRestore = async () => {
    if (!workspaceId || selectedIds.size === 0) return;

    setIsBulkRestoring(true);
    try {
      const idsToRestore = Array.from(selectedIds);
      await restoreContacts.mutateAsync({
        contactIds: idsToRestore,
        workspaceId,
      });
      // Mark all as restored and clear selection
      setRestoredIds((prev) => new Set([...prev, ...idsToRestore]));
      setSelectedIds(new Set());
    } finally {
      setIsBulkRestoring(false);
    }
  };

  const getContactName = (contact: {
    is_company: boolean;
    company_name: string | null;
    first_name: string | null;
    last_name: string | null;
  }) => {
    if (contact.is_company) {
      return contact.company_name || 'Unnamed Company';
    }
    const firstName = contact.first_name || '';
    const lastName = contact.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Unnamed Contact';
  };

  const getDaysLeftBadgeVariant = (daysLeft: number): 'default' | 'secondary' | 'destructive' => {
    if (daysLeft < 7) return 'destructive';
    if (daysLeft < 14) return 'secondary';
    return 'default';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Deleted Contacts</CardTitle>
          <CardDescription>
            View and restore contacts that have been deleted. Contacts are permanently removed after 60 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Deleted Contacts</CardTitle>
          <CardDescription>
            View and restore contacts that have been deleted. Contacts are permanently removed after 60 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-40 text-destructive">
            <AlertCircle className="h-8 w-8 mb-2" />
            <p>Error loading deleted contacts:</p>
            <p className="text-sm">{error?.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deleted Contacts</CardTitle>
        <CardDescription>
          View and restore contacts that have been deleted. Contacts are permanently removed after 60 days.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Bulk action toolbar - appears when contacts are selected */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-4 mb-4 p-3 bg-muted rounded-md">
            <span className="text-sm font-medium">
              {selectedIds.size} contact{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <Button
              variant="default"
              size="sm"
              onClick={handleBulkRestore}
              disabled={isBulkRestoring}
            >
              {isBulkRestoring ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-1" />
              )}
              Restore Selected
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={isBulkRestoring}
            >
              Clear Selection
            </Button>
          </div>
        )}

        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={(checked) => handleSelectAll(checked === true)}
                    aria-label="Select all"
                    disabled={!deletedContacts || deletedContacts.length === 0}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Deleted</TableHead>
                <TableHead>Deleted By</TableHead>
                <TableHead>Days Left</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deletedContacts && deletedContacts.length > 0 ? (
                deletedContacts.map((contact) => {
                  const isRestored = restoredIds.has(contact.id);
                  return (
                    <TableRow
                      key={contact.id}
                      className={isRestored ? 'opacity-60' : undefined}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(contact.id)}
                          onCheckedChange={(checked) =>
                            handleSelectOne(contact.id, checked as boolean)
                          }
                          aria-label={`Select ${getContactName(contact)}`}
                          disabled={isRestored}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {getContactName(contact)}
                      </TableCell>
                      <TableCell>{contact.primary_email || '-'}</TableCell>
                      <TableCell>
                        {formatDistanceToNow(new Date(contact.deleted_at), {
                          addSuffix: true,
                        })}
                      </TableCell>
                      <TableCell>{contact.deleted_by_name || 'Unknown'}</TableCell>
                      <TableCell>
                        {isRestored ? (
                          <Badge variant="default" className="bg-green-600 hover:bg-green-600">
                            Restored
                          </Badge>
                        ) : (
                          <Badge variant={getDaysLeftBadgeVariant(contact.days_until_purge)}>
                            {contact.days_until_purge} days
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(contact.id)}
                          disabled={isRestored || restoringId === contact.id || isBulkRestoring}
                        >
                          {restoringId === contact.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <RotateCcw className="h-4 w-4 mr-1" />
                          )}
                          Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <Trash2 className="h-8 w-8 mb-2" />
                      <p>No deleted contacts</p>
                      <p className="text-sm">
                        Contacts you delete will appear here for 60 days before being permanently removed.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
